/**
 * Sending what the device kept.
 *
 * The loop is deliberately dull: oldest first, one at a time, and it stops the
 * moment it meets something a person has to settle. Parallel replay would
 * reorder edits to the same record, and carrying on past a conflict would
 * leave a person with a list of decisions rather than one - which is how
 * conflict resolution turns into "keep mine" on everything.
 */

import { MAX_QUEUED_AGE_DAYS, needsAPerson, tooOldToReplay, type OfflineStore, type QueuedWrite } from './offline.js';
import { ApiError, IDEMPOTENCY_HEADER, request, type RequestSpec } from './runtime.js';

export interface ReplayReport {
  sent: number;
  /** Still here because the server answered with something only a person can settle. */
  needsAPerson: number;
  /** Still here because there was still no network. */
  waiting: number;
  refused: number;
}

/** A send that behaves like the real one; swapped in tests for the failure cases. */
export type Send = (spec: RequestSpec, idempotencyKey: string) => Promise<unknown>;

export const liveSend: Send = (spec, idempotencyKey) =>
  request(spec, { headers: { [IDEMPOTENCY_HEADER]: idempotencyKey } });

/**
 * True when this failure means "no network", as opposed to "the server said no".
 *
 * The distinction decides whether a write is kept or surfaced, so it is made on
 * evidence rather than on a guess: `fetch` rejects (rather than resolving with
 * a status) exactly when it could not complete a round trip. A 500 is the
 * server answering, and answering badly - replaying it on a timer would hide a
 * real fault behind a queue that never drains.
 */
export function isOffline(error: unknown): boolean {
  if (error instanceof ApiError) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return error instanceof TypeError || (error instanceof Error && /network|fetch|offline/i.test(error.message));
}

/**
 * Replays the queue, oldest first, stopping at the first thing needing a person.
 *
 * Returns what happened rather than throwing, because every outcome here is
 * ordinary: still offline is ordinary, a conflict is ordinary, and a refusal is
 * something to show rather than to crash on.
 */
export async function replayQueue(store: OfflineStore, send: Send = liveSend): Promise<ReplayReport> {
  const report: ReplayReport = { sent: 0, needsAPerson: 0, waiting: 0, refused: 0 };
  const pending = await store.all();

  for (const write of pending) {
    if (write.state === 'needs-you') {
      report.needsAPerson += 1;
      // Stop rather than skip. Later writes may touch the same record, and
      // applying them over an unsettled conflict decides it by accident.
      break;
    }
    if (write.state === 'refused') {
      report.refused += 1;
      continue;
    }

    /*
     * Too old to send safely.
     *
     * The server may have pruned the answer to this key by now, and a replay
     * it no longer recognises would be applied a second time - the duplicate
     * the key existed to prevent. Marked and shown rather than sent or
     * deleted: the person is the only one who can decide whether a
     * three-week-old note still matters.
     */
    if (tooOldToReplay(write)) {
      await store.put({
        ...write,
        state: 'refused',
        refusal: `This has been waiting more than ${String(MAX_QUEUED_AGE_DAYS)} days, which is longer than it can be sent safely. Check the record before entering it again.`,
      });
      report.refused += 1;
      continue;
    }

    try {
      const response = await send(write.spec, write.id);
      if (needsAPerson(response)) {
        await store.put({ ...write, state: 'needs-you', attempts: write.attempts + 1, response });
        report.needsAPerson += 1;
        break;
      }
      await store.remove(write.id);
      report.sent += 1;
    } catch (error) {
      if (isOffline(error)) {
        // Untouched, including the attempt count: there was no attempt. A
        // counter that ticked every time the browser woke up would eventually
        // look like a write that had been refused fifty times.
        report.waiting += 1;
        break;
      }
      /*
       * The server answered no, and replaying will not change its mind.
       *
       * Kept rather than dropped, in a state that says so. A write that
       * vanished because the record was deleted while the device was away
       * would take somebody's afternoon of notes with it and tell them
       * nothing; this way the words are still there to copy out.
       */
      const refusal =
        error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'It was refused.';
      await store.put({ ...write, state: 'refused', attempts: write.attempts + 1, refusal });
      report.refused += 1;
    }
  }

  return report;
}

/** A write the person has settled: send the decided values, then forget it. */
export async function resolveQueued(
  store: OfflineStore,
  write: QueuedWrite,
  decided: RequestSpec,
  send: Send = liveSend,
): Promise<unknown> {
  /*
   * A new key for a new decision.
   *
   * The old one names a request the server has already answered, and sending
   * the decided values under it would be answered from the idempotency table
   * with the conflict report all over again - the person would press Keep mine
   * and watch nothing happen.
   */
  const response = await send(decided, crypto.randomUUID());
  if (needsAPerson(response)) {
    await store.put({ ...write, spec: decided, state: 'needs-you', response, attempts: write.attempts + 1 });
    return response;
  }
  await store.remove(write.id);
  return response;
}
